import React, {useEffect, useMemo, useRef, useState} from 'react'

import {FormikHelpers, useFormik} from 'formik'
import orderBy from 'lodash/orderBy'
import {ConnectedProps} from 'react-redux'
import * as Yup from 'yup'

import config from '../../config'

import * as authActions from '../../actions/auth'
import * as modalActions from '../../actions/modal'
import {State} from '../../reducers'

import useAttachments from '../../../../commons/src/hooks/use-attachments'
import {HttpStatusError} from '../../../../commons/src/libs/http-json'
import {
  MessagePlaceholder,
  MessagePlaceholderMap,
  getSanitizedMessageAndAdditions,
} from '../../../../commons/src/libs/messages'
import {maxLength} from '../../../../commons/src/libs/validator'
import download from '../../../../commons/src/services/download'
import {captureException, captureIgnoredException} from '../../../../commons/src/services/logging'
import {connect} from '../../../../commons/src/services/loop'
import useMessageTemplate, {MentionedFileNameOption} from '../../hooks/use-message-template'
import {formatDateTime, formatFileSize, formatPrice} from '../../libs/formatter'
import {hasUnreadMessages} from '../../libs/order'
import * as internalApi from '../../services/api/internal'
import {PostMessagePayload} from '../../services/api/partner'
import * as partnerApi from '../../services/api/partner'

import {Attachment, FileId, Message, MessageAccountType} from '../../../../commons/src/types'

import accountIcon from '../../../../commons/src/assets/icons/account.svg'
import announcementIcon from '../../../../commons/src/assets/icons/announcement.svg'
import buildingIcon from '../../../../commons/src/assets/icons/building.svg'
import deleteIcon from '../../../../commons/src/assets/icons/delete.svg'
import downloadIcon from '../../../../commons/src/assets/icons/download-box.svg'
import plusIcon from '../../../../commons/src/assets/icons/plus.svg'
import sendIcon from '../../../../commons/src/assets/icons/send.svg'

import ActionBar from '../../../../commons/src/components/action-bar/ActionBar'
import Button from '../../../../commons/src/components/button'
import Grid from '../../../../commons/src/components/grid'
import GridCell from '../../../../commons/src/components/grid/GridCell'
import Headline from '../../../../commons/src/components/headline/headline'
import Icon from '../../../../commons/src/components/icon'
import LinkButton from '../../../../commons/src/components/link-button'
import MessageContentCard from '../../../../commons/src/components/message-content-card'
import MultiSelectField from '../../../../commons/src/components/multi-select-field'
import Newlines from '../../../../commons/src/components/newlines'
import OrderMessageList from '../../../../commons/src/components/order-message-list'
import MessageAttachment from '../../../../commons/src/components/order-message-list/MessageAttachment'
import MessageListItem from '../../../../commons/src/components/order-message-list/MessageListItem'
import Paragraph from '../../../../commons/src/components/paragraph'
import SelectField from '../../../../commons/src/components/select-field'
import SelectMenu from '../../../../commons/src/components/select-menu'
import SplitLayout from '../../../../commons/src/components/split-layout'
import Stack from '../../../../commons/src/components/stack'
import TextField from '../../../../commons/src/components/text-field'
import MessagesSplitLayout from '../../components/messages-split-layout/MessagesSplitLayout'
import MessagesSplitLayoutColumn from '../../components/messages-split-layout/MessagesSplitLayoutColumn'

import {AdditionalCost, Order} from '../../order-types'
import SaveButtonPartial from '../save-button-partial'

class PartnerMessageError extends Error {
  constructor(message?: string) {
    super(message || `Sending partner message failed`)
    this.name = 'PartnerMessageError'
  }
}

const initialValues = {
  text: '',
  formikGeneral: '',
}

const validationSchema = {
  text: Yup.string().label('Message to customer'),
}

const messageLinkPlaceholderMap: MessagePlaceholderMap = {
  [MessagePlaceholder.ASK_FOR_REVIEW]: {
    replacement: '',
  },
}

const mapStateToProps = (state: State) => ({
  provider: state.core.provider,
  isAdminMode: state.auth.isAdminMode,
})

const mapDispatchToProps = {
  openConfirmDeleteMessagesModal: modalActions.openConfirmDeleteMessagesModal,
  signOut: authActions.signOut,
}

const connector = connect(mapStateToProps, mapDispatchToProps)

type OwnProps = {
  order: Order
  openOverlay: () => void
  onOrderUpdate: (updatedOrder: Order) => void
  hasReview: boolean
  askForReviewEnabled: boolean
}

type Props = ConnectedProps<typeof connector> & OwnProps

const OrderMessagesPartial = ({
  order,
  provider,
  isAdminMode,
  hasReview,
  askForReviewEnabled,
  onOrderUpdate,
  openConfirmDeleteMessagesModal,
  openOverlay,
  signOut,
}: Props) => {
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isSendingMessage, setIsSendingMessage] = useState(false)

  const {attachments, fileSizeExceededCount, addAttachments, deleteAttachment, resetAttachments} =
    useAttachments(config.fileSizeLimit)

  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const timerRef = useRef<NodeJS.Timeout>()

  const {
    topic,
    onTopicChange,
    topicTemplate,
    onTopicTemplateChange,
    actionSuggestion,
    onActionSuggestionChange,
    mentionedFileNames,
    onMentionedFileNamesChange,
    currentTopicOptions,
    currentTopicTemplateOptions,
    currentActionOptions,
    templateHelper,
    showTopicTemplateInput,
    showActionSuggestionInput,
    showAffectedFilesInput,
  } = useMessageTemplate(order, provider)

  const sortedMessageListItems: (Message | AdditionalCost)[] = useMemo(() => {
    const mixedArray = [...(order.additionalCosts || []), ...(order.messages || [])]
    return orderBy(mixedArray, (item: Message | AdditionalCost) => item.created, 'desc')
  }, [order])

  const mentionedFileNameOptions: MentionedFileNameOption[] = useMemo(
    () =>
      order.models.map(model => ({
        value: model.modelId,
        label: model.fileName,
      })),
    [order]
  )

  const sanitizedCurrentTopicOptions = useMemo(() => {
    if (hasReview) {
      return currentTopicOptions.filter(option => option.value !== 'customerReview')
    }
    return currentTopicOptions
  }, [currentTopicOptions, hasReview])

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      ...initialValues,
    },
    validationSchema: Yup.object(validationSchema),
    onSubmit: async (values, formikHelpers: FormikHelpers<typeof initialValues>) => {
      if (templateHelper.hasRemainingPlaceholders(formik.values.text)) {
        formikHelpers.setFieldError(
          'formikGeneral',
          'Please replace all placeholders before submitting your message. Placeholders are inside brackets, e.g. [free text].'
        )
      } else if (
        templateHelper.mentionsAttachment(formik.values.text) &&
        Object.values(attachments).length === 0
      ) {
        formikHelpers.setFieldError(
          'formikGeneral',
          'Your message refers to an attachment, yet no attachment has been added. Please add one or remove the reference from your message.'
        )
      } else if (!maxLength(config.orderMessageMaxLength)(values.text)) {
        formikHelpers.setFieldError(
          'formikGeneral',
          `Your message length exceeds the maximum of ${config.orderMessageMaxLength} chars.`
        )
      } else {
        setIsSendingMessage(true)

        const files = Object.values(attachments).map(file => file.file)
        let payload: PostMessagePayload = {
          text: values.text,
        }

        if (files.length > 0) {
          payload = {...payload, attachments: files}
        }

        if (topicTemplate?.value === 'askForReview') {
          const reviewLink = `${config.services.craftcloud.baseUrl}/order/${order.orderId}/review/${provider?.id}`
          const linkLabel = 'Add your review here'
          payload = {
            ...payload,
            text: payload.text.replace(
              '<autofill: askForReviewLink>',
              `\n<a cc-link-type="askForReview" href="${reviewLink}">&#11088; <strong>${linkLabel}</strong></a>`
            ),
          }
        }

        try {
          await partnerApi.postMessage(order.orderId, payload)

          const updatedOrder = await partnerApi.getOrder(order.orderId)
          onOrderUpdate(updatedOrder)

          setIsSubmitted(true)

          // reset
          formikHelpers.resetForm()
          resetAttachments()
          templateHelper.resetTemplates()
        } catch (error) {
          if (error && error instanceof HttpStatusError && error.response.status === 401) {
            captureIgnoredException(error)
            signOut()
          } else {
            const errorMessage: string | undefined = (error as Error)?.message
            captureException(new PartnerMessageError(errorMessage), 'default', {
              originalError: error,
              customerText: values.text,
              providerId: provider?.id,
              filesCount: files.length,
            })
            formikHelpers.setFieldError(
              'formikGeneral',
              errorMessage || 'Oops something went wrong. Please try again!'
            )
          }
        } finally {
          setIsSendingMessage(false)
        }
      }
    },
  })

  useEffect(() => {
    if (topicTemplate) {
      formik.setFieldValue('text', templateHelper.buildMessage(topicTemplate))
    } else {
      formik.setFieldValue('text', '')
    }
    formik.validateForm()
  }, [topicTemplate, actionSuggestion, mentionedFileNames])

  useEffect(() => {
    formik.validateForm()
  }, [attachments])

  useEffect(() => {
    if (!!order && !isAdminMode && hasUnreadMessages(order)) {
      const updateOrder = async () => {
        try {
          await partnerApi.patchOrderMessagesReadStatus(order.orderId)
          const updatedOrder = await partnerApi.getOrder(order.orderId)
          onOrderUpdate(updatedOrder)
        } catch (error) {
          if (error && error instanceof HttpStatusError && error.response.status === 401) {
            captureIgnoredException(error)
            signOut()
          } else {
            captureException(
              new PartnerMessageError('Could not patch order messages read status.'),
              'default',
              {
                originalError: error,
                providerId: provider?.id,
              }
            )
          }
        }
      }

      // Mark message as unread after some time
      timerRef.current = setTimeout(() => {
        updateOrder()
      }, config.orderMessageReadTimeout)
    }

    return () => {
      clearTimeout(timerRef.current)
    }
  }, [order])

  const onFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      if (
        event.target.files.length + Object.values(attachments).length >
        config.orderMessageAttachmentMaxCount
      ) {
        formik.setFieldError(
          'formikGeneral',
          `Please be aware that we only allow a maximum of ${config.orderMessageAttachmentMaxCount} attachments per message.`
        )
      } else {
        addAttachments([...event.target.files])
        formik.setFieldError('formikGeneral', '')
      }
    }
    // Clear file input
    event.target.value = ''
  }

  const deleteFile = (fileId: FileId) => {
    deleteAttachment(fileId)
    formik.setFieldError('formikGeneral', '')
  }

  const downloadFile = (attachement: Attachment) => {
    const api = isAdminMode ? internalApi : partnerApi
    api
      .getAttachmentDownload(order.orderId, attachement.id)
      .then(response => download(response.url))
  }

  const renderAdditionalCostItem = (additionalCost: AdditionalCost) => {
    return (
      <MessageListItem
        key={additionalCost.created}
        title="Additional cost requested"
        date={formatDateTime(additionalCost.created)}
        icon={<Icon source={announcementIcon} />}
        footer={`"${additionalCost.description}"`}
        appearance={additionalCost.status.paid ? 'eventResolved' : 'event'}
        align="right"
      >
        <Stack>
          <ActionBar>
            <Paragraph size="s" minor classNames={['u-no-margin-bottom']}>
              Amount:{' '}
              <strong>
                {formatPrice(additionalCost.amounts.netPrice, additionalCost.amounts.currency, 2)}
              </strong>
            </Paragraph>
            <Paragraph size="s" minor classNames={['u-no-margin-bottom']}>
              Status: <strong>{additionalCost.status.paid ? 'Paid' : 'Awaiting Payment'}</strong>
            </Paragraph>
          </ActionBar>
        </Stack>
      </MessageListItem>
    )
  }

  const renderMessageListItem = (message: Message) => {
    const icon =
      message.author.accountType === MessageAccountType.PARTNER ? buildingIcon : accountIcon
    const authorName =
      message.author.accountType === MessageAccountType.PARTNER
        ? provider?.name || 'You'
        : `${order.customer.firstName} ${order.customer.lastName}`

    const {sanitizedMessage, messageAdditions} = getSanitizedMessageAndAdditions(
      message.text,
      messageLinkPlaceholderMap,
      reason => {
        captureException(new PartnerMessageError(reason))
      }
    )

    return (
      <MessageListItem
        key={message.created}
        title={authorName}
        date={formatDateTime(message.created)}
        unread={message.unread}
        footer={
          !!message.attachments &&
          message.attachments.length > 0 && (
            <Stack space="80">
              {message.attachments.map(attachement => (
                <MessageAttachment
                  key={attachement.id}
                  headline={
                    <LinkButton
                      onClick={() => downloadFile(attachement)}
                      disabled={isSendingMessage}
                    >
                      {attachement.name}
                    </LinkButton>
                  }
                  button={
                    <LinkButton
                      icon={<Icon source={downloadIcon} />}
                      onClick={() => downloadFile(attachement)}
                      disabled={isSendingMessage}
                    />
                  }
                />
              ))}
            </Stack>
          )
        }
        icon={<Icon source={icon} />}
        align={message.author.accountType === MessageAccountType.PARTNER ? 'right' : 'left'}
      >
        <Stack>
          <Paragraph classNames={['u-no-margin-bottom']}>
            <Newlines text={sanitizedMessage} />
          </Paragraph>
          {messageAdditions.length > 0 && <Stack>{messageAdditions}</Stack>}
        </Stack>
      </MessageListItem>
    )
  }

  const onTemplateChange = (callback: () => void) => {
    if (formik.values.text !== '' && templateHelper.isCustomMessage(formik.values.text)) {
      openConfirmDeleteMessagesModal(callback)
    } else {
      callback()
    }
  }

  // If there is only one template, preselect it
  useEffect(() => {
    if (currentTopicTemplateOptions.length === 1) {
      onTemplateChange(() => {
        onTopicTemplateChange(currentTopicTemplateOptions[0])
      })
    }
  }, [currentTopicTemplateOptions])

  const hasMessages = order.messages && order.messages.length > 0

  return (
    <MessagesSplitLayout>
      <MessagesSplitLayoutColumn
        headline={
          <>
            <Headline size="s" label="New Message" classNames={['u-no-margin-bottom']} />
            <LinkButton onClick={openOverlay} disabled={isSendingMessage}>
              Show order details
            </LinkButton>
          </>
        }
      >
        <MessageContentCard
          footer={
            <Stack space="300">
              <>
                {fileSizeExceededCount > 0 && (
                  <Paragraph
                    size="s"
                    warning
                  >{`We could not attach ${fileSizeExceededCount} of your selected files as the file size is exceeding our file size limit of ${formatFileSize(
                    config.fileSizeLimit,
                    0
                  )}.`}</Paragraph>
                )}
                {Object.values(attachments).length > 0 && (
                  <Grid space="70">
                    {Object.values(attachments).map((attachement, index) => (
                      <GridCell s={index > 2 ? 0 : undefined} m={6} key={attachement.id}>
                        <MessageAttachment
                          headline={attachement.name}
                          button={
                            <LinkButton
                              icon={<Icon source={deleteIcon} />}
                              onClick={() => deleteFile(attachement.id)}
                              disabled={isSendingMessage}
                            />
                          }
                        />
                      </GridCell>
                    ))}
                  </Grid>
                )}
              </>
              <SplitLayout
                verticalAlignment="center"
                left={
                  <>
                    <Button
                      hierarchy="secondary"
                      icon={<Icon source={plusIcon} />}
                      onClick={() => fileInputRef.current?.click()}
                      disabled={
                        isSendingMessage ||
                        Object.values(attachments).length >=
                          config.orderMessageAttachmentMaxCount ||
                        topicTemplate?.value === 'askForReview'
                      }
                    >
                      Attach files
                    </Button>
                    <input
                      type="file"
                      id="attachment-input"
                      ref={fileInputRef}
                      style={{display: 'none'}}
                      multiple
                      onChange={onFileInputChange}
                      disabled={isSendingMessage}
                    />
                  </>
                }
                right={
                  <SaveButtonPartial
                    formik={formik}
                    isSubmitted={isSubmitted}
                    icon={<Icon source={sendIcon} />}
                    iconAlign="right"
                    label="Send"
                    doneLabel="Sent"
                    submitOnEnter={false}
                    disabled={topicTemplate?.value === 'askForReview' && !askForReviewEnabled}
                  />
                }
              />
            </Stack>
          }
        >
          <Grid>
            <GridCell m={6}>
              <Stack space="70">
                <Paragraph minor size="s" classNames={['u-no-margin-bottom']}>
                  Message topic
                </Paragraph>
                <SelectField
                  placeholder="Choose a topic"
                  menu={<SelectMenu values={sanitizedCurrentTopicOptions} />}
                  value={topic}
                  onChange={option => {
                    onTemplateChange(() => {
                      onTopicChange(option)
                    })
                  }}
                  disabled={isSendingMessage}
                  tiny
                />
              </Stack>
            </GridCell>
            {showTopicTemplateInput && (
              <GridCell m={6}>
                <Stack space="70">
                  <Paragraph minor size="s" classNames={['u-no-margin-bottom']}>
                    Topic template
                  </Paragraph>
                  <SelectField
                    placeholder="Choose a template"
                    menu={<SelectMenu values={currentTopicTemplateOptions} />}
                    value={topicTemplate}
                    onChange={option => {
                      onTemplateChange(() => {
                        onTopicTemplateChange(option)
                      })
                    }}
                    disabled={isSendingMessage}
                    tiny
                  />
                </Stack>
              </GridCell>
            )}
            {showActionSuggestionInput && (
              <GridCell m={6}>
                <Stack space="70">
                  <Paragraph minor size="s" classNames={['u-no-margin-bottom']}>
                    Suggested action
                  </Paragraph>
                  <SelectField
                    placeholder="Suggest an action"
                    menu={<SelectMenu values={currentActionOptions} />}
                    value={actionSuggestion}
                    onChange={option => {
                      onTemplateChange(() => {
                        onActionSuggestionChange(option)
                      })
                    }}
                    disabled={isSendingMessage}
                    tiny
                  />
                </Stack>
              </GridCell>
            )}
            {showAffectedFilesInput && (
              <GridCell m={6}>
                <Stack space="70">
                  <Paragraph minor size="s" classNames={['u-no-margin-bottom']}>
                    Affected files
                  </Paragraph>
                  <MultiSelectField
                    placeholder="Select affected files"
                    menu={<SelectMenu values={mentionedFileNameOptions} />}
                    value={mentionedFileNames}
                    onChange={option => {
                      onTemplateChange(() => {
                        onMentionedFileNamesChange(option)
                      })
                    }}
                    disabled={isSendingMessage}
                    tiny
                  />
                </Stack>
              </GridCell>
            )}
          </Grid>
          <form onSubmit={formik.handleSubmit}>
            <TextField
              label="Your message to the customer"
              name="text"
              value={formik.values.text}
              error={formik.submitCount > 0 && !!formik.errors.text}
              onChange={(value, name) => {
                formik.setFieldValue(name as string, value)
                formik.setFieldError('formikGeneral', '')
                if (isSubmitted) {
                  setIsSubmitted(false)
                }
              }}
              onBlur={formik.handleBlur}
              disabled={isSendingMessage || topicTemplate?.uneditable}
              tiny
            />
          </form>
          <Paragraph
            minor
            size="xs"
            error={formik.values.text.length > config.orderMessageMaxLength}
            classNames={['u-no-margin-bottom', 'u-align-right']}
          >
            {`${config.orderMessageMaxLength - formik.values.text.length} characters left`}
          </Paragraph>
          {formik.errors.formikGeneral && (
            <Paragraph size="s" error>
              {formik.errors.formikGeneral}
            </Paragraph>
          )}
          {topicTemplate?.value === 'askForReview' && !askForReviewEnabled && (
            <Paragraph size="s" warning>
              {`You can ask the customer for a review if the order is not older than ${config.askForReview.maxDaysOld} days, and has been in the "Received" stage for at least ${config.askForReview.minDaysOld} days.`}
            </Paragraph>
          )}
        </MessageContentCard>
      </MessagesSplitLayoutColumn>
      <MessagesSplitLayoutColumn
        headline={<Headline size="s" label="Message History" classNames={['u-no-margin-bottom']} />}
      >
        {hasMessages && (
          <OrderMessageList>
            {sortedMessageListItems.map(item => {
              if ((item as Message).author) {
                return renderMessageListItem(item as Message)
              }
              if (
                (item as AdditionalCost).vendorId &&
                (item as AdditionalCost).amounts &&
                (item as AdditionalCost).status
              ) {
                return renderAdditionalCostItem(item as AdditionalCost)
              }
              captureException(
                new Error('Could not render item in CustomerMessageList'),
                'default',
                {item, orderNumber: order.orderNumber, providerrId: provider?.id}
              )
              return null
            })}
          </OrderMessageList>
        )}
        {!hasMessages && (
          <Paragraph minor size="s">
            No messages yet
          </Paragraph>
        )}
      </MessagesSplitLayoutColumn>
    </MessagesSplitLayout>
  )
}

export default connector(OrderMessagesPartial)
